import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useExpenseAggregates,
  useFakturowniaInvoiceAggregates,
} from 'core/hooks';
import { ChartCard, ChartsGrid } from 'molecules';

import {
  CategoryDonut,
  GroupedBarChart,
  LineWithZeroChart,
  MonthBarChart,
} from './charts';
import {
  alignMonthlySeries,
  monthlyDiff,
  splitInvoiceSeries,
} from './seriesUtils';

interface IDashboardChartsProps {
  dateFrom: string;
  dateTo: string;
}

const toSeries = (rows: { group?: string | null; total?: number | null }[]) => {
  const labels: string[] = [];
  const values: number[] = [];
  rows.forEach((r) => {
    labels.push(r.group || '—');
    values.push(r.total || 0);
  });
  return { labels, values };
};

/**
 * Six chart cards in a responsive grid (Phase 6 expansion of TZ §3.8):
 * income vs expenses, monthly profit, issued vs received, and the three
 * legacy expense breakdowns. Each card owns its query so an empty bucket
 * doesn't tear down the row. All series share the same date range from
 * the page-level preset bar.
 */
export const DashboardCharts = memo(
  ({ dateFrom, dateTo }: IDashboardChartsProps) => {
    const { t } = useTranslation();

    const { rows: paidByMonth } = useFakturowniaInvoiceAggregates({
      direction: 'issued',
      dateFrom,
      dateTo,
      groupBy: 'month',
    });
    const { rows: expensesByMonth } = useExpenseAggregates(
      dateFrom,
      dateTo,
      'month',
    );
    const { rows: byCategory } = useExpenseAggregates(
      dateFrom,
      dateTo,
      'category',
    );
    const { rows: byEntity } = useExpenseAggregates(
      dateFrom,
      dateTo,
      'legal_entity',
    );

    // For «Income vs Expenses» and «Profit», income = paid (cash actually in),
    // not gross issued — financier wants to compare real money against costs.
    const paidSeries = useMemo(
      () =>
        paidByMonth.map((r) => ({
          group: r.group,
          total: r.totalPaid || 0,
        })),
      [paidByMonth],
    );

    const incomeVsExpenses = useMemo(
      () => alignMonthlySeries(paidSeries, expensesByMonth),
      [paidSeries, expensesByMonth],
    );

    const profitSeries = useMemo(
      () => monthlyDiff(paidSeries, expensesByMonth),
      [paidSeries, expensesByMonth],
    );

    const issuedVsReceived = useMemo(
      () => splitInvoiceSeries(paidByMonth, 'totalGross', 'totalPaid'),
      [paidByMonth],
    );

    const expensesSeries = useMemo(() => toSeries(expensesByMonth), [
      expensesByMonth,
    ]);
    const categorySeries = useMemo(() => toSeries(byCategory), [byCategory]);
    const entitySeries = useMemo(() => toSeries(byEntity), [byEntity]);

    return (
      <ChartsGrid>
        <ChartCard title={t('pages.financeDashboard.charts.incomeVsExpenses')}>
          {incomeVsExpenses.labels.length ? (
            <GroupedBarChart
              labels={incomeVsExpenses.labels}
              aValues={incomeVsExpenses.aValues}
              bValues={incomeVsExpenses.bValues}
              aLabel={t('pages.financeDashboard.charts.legendIncome')}
              bLabel={t('pages.financeDashboard.charts.legendExpenses')}
              aColor="#16a34a"
              bColor="#ef4444"
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>

        <ChartCard title={t('pages.financeDashboard.charts.profit')}>
          {profitSeries.labels.length ? (
            <LineWithZeroChart
              labels={profitSeries.labels}
              values={profitSeries.values}
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>

        <ChartCard title={t('pages.financeDashboard.charts.issuedVsReceived')}>
          {issuedVsReceived.labels.length ? (
            <GroupedBarChart
              labels={issuedVsReceived.labels}
              aValues={issuedVsReceived.aValues}
              bValues={issuedVsReceived.bValues}
              aLabel={t('pages.financeDashboard.charts.legendIssued')}
              bLabel={t('pages.financeDashboard.charts.legendPaid')}
              aColor="#2563eb"
              bColor="#16a34a"
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>

        <ChartCard title={t('pages.financeDashboard.charts.byCategory')}>
          {categorySeries.values.length ? (
            <CategoryDonut
              labels={categorySeries.labels}
              values={categorySeries.values}
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>

        <ChartCard title={t('pages.financeDashboard.charts.byMonth')}>
          {expensesSeries.values.length ? (
            <MonthBarChart
              labels={expensesSeries.labels}
              values={expensesSeries.values}
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>

        <ChartCard title={t('pages.financeDashboard.charts.byLegalEntity')}>
          {entitySeries.values.length ? (
            <CategoryDonut
              labels={entitySeries.labels}
              values={entitySeries.values}
            />
          ) : (
            <Empty t={t} />
          )}
        </ChartCard>
      </ChartsGrid>
    );
  },
);

const Empty = ({ t }: { t: (k: string) => string }) => (
  <div
    style={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94a3b8',
      fontSize: 13,
    }}
  >
    {t('pages.financeDashboard.charts.empty')}
  </div>
);
